import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useTerritoryJson } from "@features/onboarding/utility-setup/hooks";

interface ServiceAreaFilters {
  page?: number;
  limit?: number;
  remote_utility_id?: number;
  config_level?: string;
  show_inactive?: boolean;
  search_data?: string;
  created_by?: string;
  parent_name?: string;
  tab?: string;
}

interface ServiceAreaFilterPanelProps {
  currentFilters: ServiceAreaFilters;
  onApplyFilters: (filters: Partial<ServiceAreaFilters>) => void;
  onResetFilters: () => void;
  entityType?: string;
  configLevel?: string;
}

const getParentConfigLevel = (levelOrType: string): string => {
  const str = String(levelOrType || "")
    .toLowerCase()
    .trim();
  switch (str) {
    case "unit":
    case "11":
      return "premise";
    case "premise":
    case "premises":
    case "10":
      return "sub_area";
    case "sub_area":
    case "sub-area":
    case "sub-areas":
    case "sub_areas":
    case "subarea":
    case "9":
      return "area";
    case "area":
    case "areas":
    case "8":
      return "division";
    case "division":
    case "7":
      return "zone";
    case "zone":
    case "6":
      return "county";
    case "county":
    case "city":
    case "city/county":
    case "5":
      return "state";
    case "state":
    case "4":
      return "country";
    case "country":
    case "3":
      return "region";
    case "region":
    case "2":
    default:
      return "";
  }
};

export const ServiceAreaFilterPanel: React.FC<ServiceAreaFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  entityType = "",
  configLevel = "",
}) => {
  const targetLevel = configLevel || entityType;
  const parentLevel = useMemo(
    () => getParentConfigLevel(targetLevel),
    [targetLevel],
  );

  const { data: parentOptionsData, isLoading: isLoadingParents } =
    useTerritoryJson(parentLevel);

  const parentOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [
      { label: "All Parents", value: "all" },
    ];
    const items =
      parentOptionsData?.result ||
      (Array.isArray(parentOptionsData) ? parentOptionsData : []);

    if (Array.isArray(items) && items.length > 0) {
      items.forEach((item: any) => {
        const nameVal = item.name || item.code;
        if (nameVal) {
          options.push({
            label: item.name,
            value: item.code,
          });
        }
      });
    }

    return options;
  }, [parentOptionsData]);

  const form = useForm({
    defaultValues: {
      status:
        typeof currentFilters.show_inactive === "boolean"
          ? currentFilters.show_inactive
            ? "active"
            : "inactive"
          : "all",
      parent_name: currentFilters.parent_name || "all",
    },
  });

  useEffect(() => {
    form.reset({
      status:
        typeof currentFilters.show_inactive === "boolean"
          ? currentFilters.show_inactive
            ? "active"
            : "inactive"
          : "all",
      parent_name: currentFilters.parent_name || "all",
    });
  }, [currentFilters, form]);

  const fields: FormField[] = useMemo(() => {
    const fieldList: FormField[] = [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All Status",
        options: [
          { label: "All Status", value: "all" },
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
      },
    ];

    if (parentLevel) {
      fieldList.push({
        name: "parent_name",
        label: "Parent Name",
        type: "select",
        placeholder: isLoadingParents ? "Loading parents..." : "Select Parent",
        options: parentOptions,
        disabled: isLoadingParents || parentOptions.length <= 1,
      });
    }

    return fieldList;
  }, [parentLevel, isLoadingParents, parentOptions]);

  const handleApply = (data: any) => {
    const filtersToApply: Partial<ServiceAreaFilters> = {};

    if (data.status === "active") {
      filtersToApply.show_inactive = true;
    } else if (data.status === "inactive") {
      filtersToApply.show_inactive = false;
    }

    if (data.parent_name && data.parent_name !== "all") {
      filtersToApply.parent_name = data.parent_name;
    }

    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    form.reset({
      status: "all",
      parent_name: "all",
    });
    onResetFilters();
  };

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={fields}
        form={form}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
        onSubmit={handleApply}
        onReset={handleReset}
        config={{
          showResetButton: true,
          resetButtonText: "Reset Filters",
          submitButtonText: "Apply Filters",
          classes: {
            buttonContainer:
              "flex justify-end gap-2 pt-4 border-t border-gray-200 col-span-full",
          },
        }}
      />
    </div>
  );
};
